import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutContent, faqs } from '@/mocks/data';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于亲密指南',
  description: aboutContent.introduction,
  url: 'https://climaxpro.github.io/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '亲密指南',
    url: 'https://climaxpro.github.io',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '亲密指南',
  url: 'https://climaxpro.github.io',
  description: aboutContent.tagline,
  contactPoint: {
    '@type': 'ContactPoint',
    email: aboutContent.contactEmail,
    contactType: 'customer service',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://climaxpro.github.io/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://climaxpro.github.io/about' },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const res = await fetch('https://readdy.ai/api/form/d9u3uml3pcjqs2fcslig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormStatus('error');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <Layout>
      <SEOHead
        title="关于我们 | 亲密指南 - 专业的性健康教育平台"
        description={aboutContent.introduction}
        keywords="亲密指南,性健康教育,关于我们,品牌介绍,性教育平台,专业健康知识"
        canonicalUrl="/about"
        ogType="website"
        schema={{ ...aboutSchema, ...orgSchema, ...faqSchema, ...breadcrumbSchema }}
      />

      <div className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 mb-6">
              关于{aboutContent.brandName}
            </h1>
            <p className="text-lg text-primary-600 font-medium mb-8">
              {aboutContent.tagline}
            </p>
            <p className="text-base text-foreground-700 leading-relaxed max-w-2xl mx-auto">
              {aboutContent.introduction}
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-lightbulb-line text-2xl text-primary-600"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-6">
              我们的使命
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              {aboutContent.mission}
            </p>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 text-center mb-12">
              核心价值
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutContent.values.map((val, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${val.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {val.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 text-center mb-12">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 flex-shrink-0 w-5 h-5 flex items-center justify-center ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <div className="border-t border-background-200/70 pt-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">{aboutContent.contactEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-600">{aboutContent.contactWechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-anti-spam"
                />
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="你的名字"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="你的邮箱"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="你的留言..."
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all duration-300 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">消息已发送！感谢你的联系，我们会尽快回复。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}